import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { BedDouble, Sailboat, UtensilsCrossed, ShoppingBag, MapPin, ArrowRight } from "lucide-react";
import { Layout } from "../components/Layout";
import { SponsorSlot } from "../components/SponsorSlot";
import { eur } from "../lib/format";
import * as api from "../api";

const DEST = "vieste";
const HERO = "https://images.unsplash.com/photo-1590523277543-a94d2e4eb00b?q=85&w=1600";

function Section({ id, icon: Icon, title, subtitle, items, render, cta }) {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10 border-t border-slate-100" data-testid={`dest-section-${id}`}>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 text-secondary"><Icon className="h-5 w-5" /><span className="text-xs font-bold uppercase tracking-[0.2em]">{subtitle}</span></div>
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-slate-900 mt-1">{title}</h2>
        </div>
        {items.length > 0 && cta && <Link to={cta.to} className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-primary">{cta.label} <ArrowRight className="h-4 w-4" /></Link>}
      </div>
      {items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 p-10 text-center text-slate-400 text-sm" data-testid={`dest-empty-${id}`}>In arrivo su Vieste. Stiamo selezionando le migliori proposte del territorio.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">{items.slice(0, 4).map(render)}</div>
      )}
    </section>
  );
}

const Card = ({ to, img, title, meta, price }) => (
  <Link to={to} className="group rounded-2xl overflow-hidden border border-slate-100 bg-white hover:shadow-lg transition-shadow">
    <div className="h-40 bg-slate-100 overflow-hidden">{img && <img src={img} alt="" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />}</div>
    <div className="p-4">
      <h3 className="font-heading font-semibold text-slate-900 truncate">{title}</h3>
      {meta && <p className="text-xs text-slate-500 mt-1 truncate">{meta}</p>}
      {price != null && <p className="text-sm font-bold text-primary mt-2">{price}</p>}
    </div>
  </Link>
);

export default function Destination() {
  const { data: accommodations = [] } = useQuery({ queryKey: ["dest-acc", DEST], queryFn: () => api.searchAccommodations({ destination: DEST }) });
  const { data: experiences = [] } = useQuery({ queryKey: ["dest-exp", DEST], queryFn: () => api.getExperiences({ destination: DEST, limit: 8 }) });
  const { data: restaurants = [] } = useQuery({ queryKey: ["dest-rest", DEST], queryFn: () => api.getRestaurants({ destination: DEST }) });
  const { data: products = [] } = useQuery({ queryKey: ["dest-prod", DEST], queryFn: () => api.getProducts({ destination: DEST }) });

  return (
    <Layout>
      <div className="relative h-[42vh] min-h-[320px] w-full overflow-hidden" data-testid="destination-hero">
        <img src={HERO} alt="Vieste" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 max-w-7xl mx-auto px-4 sm:px-6 pb-8">
          <div className="flex items-center gap-2 text-white/80 text-sm"><MapPin className="h-4 w-4" /> Gargano · Puglia</div>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-white mt-2">Vieste</h1>
          <p className="text-white/90 text-base md:text-lg mt-2 max-w-2xl">Dove dormire, cosa fare, dove mangiare e i prodotti tipici della perla del Gargano.</p>
        </div>
      </div>

      <SponsorSlot placement="DESTINAZIONE" className="pt-8" />

      <Section id="dormire" icon={BedDouble} subtitle="Dove dormire" title="Alloggi a Vieste" items={accommodations}
        cta={{ to: "/alloggi?destination=vieste", label: "Tutti gli alloggi" }}
        render={(x) => <Card key={x.id || x.slug} to={`/alloggi/${x.slug}`} img={x.cover || x.images?.[0]} title={x.name} meta={[x.city, x.type].filter(Boolean).join(" · ")} price={x.price_from != null ? `da ${eur(x.price_from)} / notte` : null} />} />

      <Section id="fare" icon={Sailboat} subtitle="Cosa fare" title="Esperienze a Vieste" items={experiences}
        cta={{ to: "/esperienze", label: "Tutte le esperienze" }}
        render={(x) => <Card key={x.id || x.slug} to={`/esperienza/${x.slug}`} img={x.images?.[0]} title={x.title} meta={x.category} price={x.price != null ? `da ${eur(x.price)}` : null} />} />

      <Section id="mangiare" icon={UtensilsCrossed} subtitle="Dove mangiare" title="Ristoranti a Vieste" items={restaurants}
        cta={{ to: "/dove-mangiare", label: "Tutti i ristoranti" }}
        render={(x) => <Card key={x.id || x.slug} to={`/ristoranti/${x.slug}`} img={x.cover || x.gallery?.[0]} title={x.brand_name} meta={[x.subcategory, x.city].filter(Boolean).join(" · ")} />} />

      <Section id="prodotti" icon={ShoppingBag} subtitle="Prodotti tipici" title="Dal territorio" items={products}
        cta={{ to: "/shop", label: "Tutti i prodotti" }}
        render={(x) => <Card key={x.id || x.slug} to={`/shop/${x.slug}`} img={x.images?.[0]} title={x.name} meta={x.category} price={x.price != null ? eur(x.price) : null} />} />

      <div className="h-10" />
    </Layout>
  );
}
